import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useGoalEarnings } from "@/apps/goals/hooks/useGoalEarnings";
import { requestPersonalGoalWithdraw } from "@/apps/goals/services/goalsApi";
import { useStartupParamsStore } from "@/store/startupParams";
import Button from "@/ui/Button/Button";
import Modal from "@/ui/Modal/Modal";
import Typography from "@/ui/Typography/Typography";
import cls from "./styles.module.scss";

const RUBLE_SIGN = "\u20bd";

const formatMoney = (amount: number) =>
  new Intl.NumberFormat("ru-RU").format(amount);

export const WithdrawButton = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const yandexId = useStartupParamsStore((state) => state.auth.yandexId);
  const { earnings, setEarnings, isLoading } = useGoalEarnings(yandexId);
  const availableToWithdraw = earnings?.available_to_withdraw ?? 0;
  const canWithdraw = availableToWithdraw > 0;

  const closeModal = () => setIsOpen(false);

  const withdraw = async () => {
    if (!yandexId || !canWithdraw || isSubmitting || isSubmitted) return;

    setIsSubmitting(true);

    try {
      await requestPersonalGoalWithdraw(yandexId);
      setEarnings((currentEarnings) =>
        currentEarnings
          ? {
              ...currentEarnings,
              total_withdrawn:
                currentEarnings.total_withdrawn +
                currentEarnings.available_to_withdraw,
              available_to_withdraw: 0,
            }
          : currentEarnings
      );
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        className={cls.trigger}
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label={t("goals.withdraw.ariaLabel", {
          amount: formatMoney(availableToWithdraw),
        })}
      >
        <img className={cls.coin} src="/goals/coin.png" alt="" />
        <span className={cls.amount}>
          {formatMoney(availableToWithdraw)} {RUBLE_SIGN}
        </span>
      </button>
      <Modal isOpen={isOpen} onClose={closeModal}>
        <div className={cls.modalContent}>
          <Typography
            component="h2"
            size="md"
            weight="semibold"
            color="var(--color-text-secondary)"
          >
            {t("goals.withdraw.title")}
          </Typography>

          <div className={cls.breakdown}>
            <div className={cls.breakdownRow}>
              <Typography size="md" weight="semibold">
                {t("goals.withdraw.totalEarned")}
              </Typography>
              <Typography
                size="md"
                weight="semibold"
                color="var(--color-primary)"
              >
                {formatMoney(earnings?.total_earned ?? 0)} {RUBLE_SIGN}
              </Typography>
            </div>

            <div className={cls.breakdownRow}>
              <Typography size="md" weight="semibold">
                {t("goals.withdraw.totalWithdrawn")}
              </Typography>
              <Typography
                size="md"
                weight="semibold"
                color="var(--color-primary)"
              >
                {formatMoney(earnings?.total_withdrawn ?? 0)} {RUBLE_SIGN}
              </Typography>
            </div>

            <div className={cls.breakdownRow}>
              <Typography
                size="md"
                weight="semibold"
                color="var(--color-text-secondary)"
              >
                {t("goals.withdraw.available")}
              </Typography>
              <Typography
                size="md"
                weight="semibold"
                color="var(--color-text-secondary)"
              >
                {formatMoney(availableToWithdraw)} {RUBLE_SIGN}
              </Typography>
            </div>
          </div>

          <Button
            className={isSubmitted ? cls.submittedButton : undefined}
            label={
              isSubmitted
                ? t("goals.withdraw.requestSent")
                : t("goals.withdraw.withdrawMoney")
            }
            loading={isLoading || isSubmitting}
            disabled={isSubmitted || !yandexId || !canWithdraw}
            onClick={withdraw}
          />
        </div>
      </Modal>
    </>
  );
};
